/**
 * Own-profile WhatsApp verification (#3874).
 *
 * The server is the only source of truth for the status. Starting verification
 * returns a challenge and the status `pending`; it becomes `verified` only when
 * the workshop's WhatsApp receives that challenge from the phone, which this
 * hook learns by asking the server again (polling while pending). Nothing here
 * ever marks anything verified on its own.
 *
 * Everything is keyed by the signed-in account (tenant + user). A response that
 * arrives after the account changed — logout/login as someone else, another
 * workshop — belongs to the previous account and is dropped: it neither shows
 * its status nor its challenge to the new one. The challenge itself is held in
 * memory only, for as long as the server says it is pending and unexpired.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '@/lib/api';

// Mirrors `packages/shared/src/staff-whatsapp-identity.ts`. The portal does not
// import the shared root (it pulls node-only code), so the response shapes are
// restated here, as for other mirrored contracts.
export type StaffWhatsAppVerificationStatus = 'unverified' | 'pending' | 'verified' | 'revoked';
export type StaffWhatsAppStartErrorCode =
  | 'whatsapp_not_configured'
  | 'number_missing'
  | 'number_invalid'
  | 'number_taken'
  | 'already_verified'
  | 'rate_limited';

export interface StaffWhatsAppVerificationState {
  status: StaffWhatsAppVerificationStatus;
  number: string | null;
  verifiedAt: string | null;
  revokedAt: string | null;
  expiresAt: string | null;
  destination: { phoneNumber: string } | null;
}

export interface StaffWhatsAppVerificationStarted {
  status: 'pending';
  number: string;
  challenge: string;
  expiresAt: string;
  destination: { phoneNumber: string };
  whatsappLink: string;
}

const ENDPOINT = '/users/me/whatsapp-verification';
/** How often the pending status is re-read from the server. */
export const STAFF_WHATSAPP_POLL_MS = 3000;

export const staffWhatsAppVerificationKey = (accountKey: string | null) =>
  ['staff-whatsapp-verification', accountKey] as const;

export interface UseStaffWhatsAppVerification {
  /** The server's last answer for THIS account; undefined until it has one. */
  state: StaffWhatsAppVerificationState | undefined;
  isLoading: boolean;
  loadFailed: boolean;
  refresh: () => void;
  /** The challenge of a verification started in this session, while it is still pending. */
  challenge: StaffWhatsAppVerificationStarted | null;
  start: () => void;
  isStarting: boolean;
  /** Why the last start was refused (a server code), or `generic`. */
  startError: StaffWhatsAppStartErrorCode | 'generic' | null;
  revoke: () => void;
  isRevoking: boolean;
  revokeFailed: boolean;
}

function startErrorCode(error: unknown): StaffWhatsAppStartErrorCode | 'generic' {
  if (error instanceof ApiError || (typeof error === 'object' && error !== null && 'status' in error)) {
    const code = (error as { data?: { code?: unknown } }).data?.code;
    if (typeof code === 'string') return code as StaffWhatsAppStartErrorCode;
  }
  return 'generic';
}

export function useStaffWhatsAppVerification(accountKey: string | null): UseStaffWhatsAppVerification {
  const queryClient = useQueryClient();

  // The account the hook is CURRENTLY for. A mutation captures the key it was
  // started under and compares against this when it settles.
  const currentKey = useRef(accountKey);
  currentKey.current = accountKey;

  const [issued, setIssued] = useState<{ accountKey: string; started: StaffWhatsAppVerificationStarted } | null>(null);
  const [startError, setStartError] = useState<{ accountKey: string; code: StaffWhatsAppStartErrorCode | 'generic' } | null>(null);
  const [revokeFailed, setRevokeFailed] = useState<string | null>(null);

  // Nothing carried over to another account.
  useEffect(() => {
    setIssued((previous) => (previous && previous.accountKey !== accountKey ? null : previous));
    setStartError((previous) => (previous && previous.accountKey !== accountKey ? null : previous));
    setRevokeFailed(null);
  }, [accountKey]);

  const query = useQuery({
    queryKey: staffWhatsAppVerificationKey(accountKey),
    queryFn: () => api.get<StaffWhatsAppVerificationState>(ENDPOINT),
    enabled: accountKey !== null,
    // Always the server's view; a cached "verified" must not outlive a revoke.
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: true,
    refetchInterval: (q) => (q.state.data?.status === 'pending' ? STAFF_WHATSAPP_POLL_MS : false),
  });

  const startMutation = useMutation({
    mutationFn: (_vars: { accountKey: string }) => api.post<StaffWhatsAppVerificationStarted>(ENDPOINT),
    onSuccess: (started, vars) => {
      if (currentKey.current !== vars.accountKey) return; // late result of another account
      setStartError(null);
      setIssued({ accountKey: vars.accountKey, started });
      // Ask the server what it now says; a started challenge is `pending`, not verified.
      void queryClient.invalidateQueries({ queryKey: staffWhatsAppVerificationKey(vars.accountKey) });
    },
    onError: (error, vars) => {
      if (currentKey.current !== vars.accountKey) return;
      setStartError({ accountKey: vars.accountKey, code: startErrorCode(error) });
      void queryClient.invalidateQueries({ queryKey: staffWhatsAppVerificationKey(vars.accountKey) });
    },
  });

  const revokeMutation = useMutation({
    mutationFn: (_vars: { accountKey: string }) => api.delete<StaffWhatsAppVerificationState>(ENDPOINT),
    onSuccess: (next, vars) => {
      if (currentKey.current !== vars.accountKey) return;
      setRevokeFailed(null);
      setIssued(null);
      queryClient.setQueryData(staffWhatsAppVerificationKey(vars.accountKey), next);
    },
    onError: (_error, vars) => {
      if (currentKey.current !== vars.accountKey) return;
      setRevokeFailed(vars.accountKey);
      void queryClient.invalidateQueries({ queryKey: staffWhatsAppVerificationKey(vars.accountKey) });
    },
  });

  const state = accountKey === null ? undefined : query.data;

  // The challenge is shown only while the server agrees it is pending, and
  // only until it expires. Verified, revoked, unverified or expired: it is gone.
  const challenge =
    accountKey !== null &&
    issued !== null &&
    issued.accountKey === accountKey &&
    state?.status === 'pending' &&
    Date.parse(issued.started.expiresAt) > Date.now()
      ? issued.started
      : null;

  const start = useCallback(() => {
    if (accountKey === null || startMutation.isPending) return;
    setStartError(null);
    startMutation.mutate({ accountKey });
  }, [accountKey, startMutation]);

  const revoke = useCallback(() => {
    if (accountKey === null || revokeMutation.isPending) return;
    revokeMutation.mutate({ accountKey });
  }, [accountKey, revokeMutation]);

  const refetch = query.refetch;
  const refresh = useCallback(() => {
    void refetch();
  }, [refetch]);

  return {
    state,
    isLoading: accountKey !== null && query.isLoading,
    loadFailed: accountKey !== null && query.isError && state === undefined,
    refresh,
    challenge,
    start,
    isStarting: startMutation.isPending,
    startError: startError && startError.accountKey === accountKey ? startError.code : null,
    revoke,
    isRevoking: revokeMutation.isPending,
    revokeFailed: revokeFailed !== null && revokeFailed === accountKey,
  };
}
